import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Loader2, MessageSquare } from "lucide-react";
import { useCreateOrUpdateProfile } from "@/hooks/auth/useCreateOrUpdateProfile";

const InviteSignup = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { createOrUpdateProfile } = useCreateOrUpdateProfile();

  // Get invitation parameters
  const phoneNumber = searchParams.get('phone') || '';
  const inviteRole = searchParams.get('invite') || 'vendor';

  // Form states
  const [otp, setOtp] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [step, setStep] = useState<'verify' | 'setup'>('verify');
  const [invitationData, setInvitationData] = useState<any>(null);

  useEffect(() => {
    if (!phoneNumber) {
      toast({
        title: "Invalid Invitation",
        description: "No phone number provided in invitation link.",
        variant: "destructive",
      });
      navigate('/auth');
    }
  }, [phoneNumber, navigate, toast]);

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!otp || otp.length !== 6) {
      setValidationError("Please enter a valid 6-digit OTP");
      return;
    }

    setLoading(true);
    setValidationError("");

    try {
      console.log("Verifying custom OTP for phone:", phoneNumber);
      
      // Use our custom OTP verification function - just verify the code, don't create account yet
      const { data: verifyData, error: verifyError } = await supabase.rpc('verify_and_claim_otp_invitation', {
        p_phone_number: phoneNumber,
        p_otp_code: otp,
        p_email: '', // Empty for now, will be filled in next step
        p_password: '', // Empty for now, will be filled in next step  
        p_display_name: '' // Empty for now, will be filled in next step
      });

      if (verifyError) {
        console.error("OTP verification error:", verifyError);
        setValidationError("Failed to verify OTP. Please check your code and try again.");
        return;
      }

      if (!verifyData?.[0]?.success) {
        setValidationError(verifyData?.[0]?.message || "Invalid or expired OTP code");
        return;
      }

      console.log("OTP verified successfully");

      // Check for invitation data to pre-fill form
      const { data: inviteData, error: inviteError } = await supabase
        .from('sms_invitations')
        .select('*')
        .eq('phone_number', phoneNumber)
        .eq('invitation_status', 'claimed') // Should be claimed after verification
        .maybeSingle();

      if (inviteError) {
        console.error("Error fetching invitation:", inviteError);
      }

      setInvitationData(inviteData);
      
      if (inviteData?.vendor_name) {
        setDisplayName(inviteData.vendor_name);
      }

      toast({
        title: "Phone Verified!",
        description: "Please complete your account setup.",
      });

      setStep('setup');

    } catch (error: any) {
      console.error("Error verifying OTP:", error);
      setValidationError(error.message || "Failed to verify OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password || !confirmPassword || !displayName) {
      setValidationError("All fields are required");
      return;
    }

    if (password !== confirmPassword) {
      setValidationError("Passwords do not match");
      return;
    }

    if (displayName.length < 3) {
      setValidationError("Display name must be at least 3 characters long");
      return;
    }

    setLoading(true);
    setValidationError("");

    try {
      console.log("Creating account with email:", email);

      // Create user account with email/password
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email: email,
        password: password,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
          data: {
            display_name: displayName,
            phone: phoneNumber,
            invitation_role: inviteRole,
            sms_invitation: 'true'
          }
        }
      });

      if (signUpError) {
        console.error("Error creating account:", signUpError);
        setValidationError(signUpError.message);
        return;
      }

      if (!signUpData.user) {
        setValidationError("Failed to create account");
        return;
      }

      console.log("Account created successfully:", signUpData.user.id);

      // Create user profile
      await createOrUpdateProfile(signUpData.user.id, {
        display_name: displayName,
        email: email
      });

      // Claim invitation and assign role using OTP function
      const { data: claimResult, error: claimError } = await supabase.rpc('claim_sms_invitation_otp', {
        p_phone_number: phoneNumber,
        p_user_id: signUpData.user.id,
        p_role: inviteRole
      });

      if (claimError) {
        console.error("Failed to claim invitation:", claimError);
        toast({
          variant: "destructive",
          title: "Warning",
          description: "Account created but invitation claiming failed. Please contact support.",
        });
      } else if (claimResult) {
        console.log("Invitation claimed successfully");
        toast({
          title: "Role Assigned!",
          description: `Your ${inviteRole} role has been activated.`,
        });
      }

      toast({
        title: "Account Created Successfully!",
        description: `Welcome! Your ${inviteRole} account is ready to use.`,
      });
      
      // Redirect to appropriate dashboard
      setTimeout(() => {
        if (inviteRole === 'vendor') {
          navigate('/vendor');
        } else {
          navigate('/');
        }
      }, 2000);
      
    } catch (error: any) {
      console.error("Error completing signup:", error);
      setValidationError(error.message || "Failed to complete account setup");
    } finally {
      setLoading(false);
    }
  };

  if (step === 'verify') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1 text-center">
            <div className="flex justify-center mb-2">
              <MessageSquare className="h-8 w-8 text-primary" />
            </div>
            <CardTitle className="text-2xl">Verify Your Phone</CardTitle>
            <CardDescription>
              Enter the 6-digit code sent to {phoneNumber}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleVerifyOtp} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="otp">Verification Code</Label>
                <Input
                  id="otp"
                  type="text"
                  placeholder="Enter 6-digit code"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6}
                  className="text-center text-lg tracking-widest"
                  required
                />
              </div>

              {validationError && (
                <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                  {validationError}
                </div>
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={loading || otp.length !== 6}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify Code'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 text-center">
          <div className="flex justify-center mb-2">
            <MessageSquare className="h-8 w-8 text-primary" />
          </div>
          <CardTitle className="text-2xl">Complete Your Account</CardTitle>
          <CardDescription>
            You've been invited to join as a {inviteRole}
            {phoneNumber && (
              <div className="text-sm text-muted-foreground mt-1">
                Phone: {phoneNumber} ✓
              </div>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleCompleteSignup} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="displayName">Full Name</Label>
              <Input
                id="displayName"
                type="text"
                placeholder="Enter your full name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Create a password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Confirm your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>

            {validationError && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                {validationError}
              </div>
            )}

            <div className="bg-muted/50 p-3 rounded-md text-sm text-muted-foreground">
              <strong>SMS Invitation Benefits:</strong>
              <ul className="mt-1 space-y-1 text-xs">
                <li>• Phone number verified</li>
                <li>• Instant {inviteRole} access</li>
                <li>• Ready to use immediately</li>
              </ul>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating Account...
                </>
              ) : (
                `Complete ${inviteRole} Setup`
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default InviteSignup;